/*
 * ******************************************************************
 * Copyright (c) 2022-2024 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Injectable } from "@angular/core";
import { HttpClient, HttpContext, HttpContextToken, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";

export const SHOW_NOTIFICATION_TOKEN: HttpContextToken<boolean> = new HttpContextToken<boolean>(() => true);

/**
 * Wrapper around Http service to be used by DPS UI application to access backend services
 */
// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class HttpWrapperService {

    constructor(public http: HttpClient) {
    }

    public get<T>(url: string,
                  showErrorNotification: boolean = false,
                  plainText: boolean = false): Observable<T> {
        if (plainText) {
            return this.http.get(url, {
                context: new HttpContext().set(SHOW_NOTIFICATION_TOKEN, showErrorNotification),
                responseType: "text"
            }) as Observable<T>;
        }
        return this.http.get<T>(url,
                                { context: new HttpContext().set(SHOW_NOTIFICATION_TOKEN, showErrorNotification) });
    }

    public post<T>(url: string,
                   spec: any,
                   showErrorNotification: boolean = true,
                   plainText: boolean = false,
                   headers?: any): Observable<T> {
        if (plainText) {
            return this.http.post(url, spec, {
                context: new HttpContext().set(SHOW_NOTIFICATION_TOKEN, showErrorNotification),
                responseType: "text"
            }) as Observable<T>;
        }
        return this.http.post<T>(url, spec, {
            headers: new HttpHeaders(headers),
            context: new HttpContext().set(SHOW_NOTIFICATION_TOKEN, showErrorNotification)
        });
    }

    public patch<T>(url: string, spec: any, showErrorNotification: boolean = true): Observable<T> {
        return this.http.patch<T>(url, spec,
                                  { context: new HttpContext().set(SHOW_NOTIFICATION_TOKEN, showErrorNotification) });
    }

    // Angular http.delete does not accept a request body, so this won't work:
    // return this.http.delete<TaskV2>(path, options)
    // Use the http.request function instead so we can pass in a request body.
    // return this.http.request<TaskV2>('delete', path, {body: {}})
    public delete<T>(path: string, spec: any = null, showErrorNotification: boolean = true): Observable<T> {

        return this.http.request<T>("delete",
                                    path,
                                    {
                                        body: spec,
                                        context: new HttpContext().set(SHOW_NOTIFICATION_TOKEN, showErrorNotification)
                                    });
    }
}
